import { currentAppSurface } from './appSurface'

/**
 * Worker web app installability + Service Worker lifecycle.
 *
 * The worker app shell is registered on the WORKFORCE surface only; the
 * consumer Social host keeps its own separate worker and manifest and is left
 * untouched. Update handling is safe by design: a new worker is installed in
 * the background and only takes over on the next navigation, so a punch in
 * flight is never interrupted by a swap.
 */

export const WORKER_SERVICE_WORKER_URL = '/worker-service-worker.js'
export const WORKER_MANIFEST_URL = '/worker.webmanifest'
export const OFFLINE_SYNC_TAG = 'punch-worker-offline-sync'

export interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferredInstallPrompt: InstallPromptEvent | null = null
const installPromptListeners = new Set<(available: boolean) => void>()

export function serviceWorkerSupported(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator
}

export function installWorkerManifestLink(): (() => void) | undefined {
  if (typeof document === 'undefined' || currentAppSurface() !== 'workforce') return undefined
  if (document.querySelector('link[data-worker-pwa="manifest"]')) return undefined
  const link = document.createElement('link')
  link.rel = 'manifest'
  link.href = WORKER_MANIFEST_URL
  link.dataset.workerPwa = 'manifest'
  document.head.appendChild(link)
  return () => link.remove()
}

export async function registerWorkerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === 'undefined' || !serviceWorkerSupported()) return null
  if (currentAppSurface() !== 'workforce') return null
  try {
    return await navigator.serviceWorker.register(WORKER_SERVICE_WORKER_URL, { scope: '/' })
  } catch {
    // Installability is an enhancement. The worker app remains fully usable as
    // an ordinary page when registration is blocked.
    return null
  }
}

export async function currentWorkerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!serviceWorkerSupported()) return null
  try {
    return (await navigator.serviceWorker.getRegistration('/')) || null
  } catch {
    return null
  }
}

/** Capture Chrome/Edge's deferred install prompt so the UI can offer Install. */
export function watchInstallAvailability(): () => void {
  if (typeof window === 'undefined') return () => undefined
  const onBeforeInstallPrompt = (event: Event) => {
    event.preventDefault()
    deferredInstallPrompt = event as InstallPromptEvent
    installPromptListeners.forEach((listener) => listener(true))
  }
  const onInstalled = () => {
    deferredInstallPrompt = null
    installPromptListeners.forEach((listener) => listener(false))
  }
  window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
  window.addEventListener('appinstalled', onInstalled)
  return () => {
    window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
    window.removeEventListener('appinstalled', onInstalled)
  }
}

export function onInstallAvailabilityChange(listener: (available: boolean) => void): () => void {
  installPromptListeners.add(listener)
  return () => installPromptListeners.delete(listener)
}

export function installPromptAvailable(): boolean {
  return deferredInstallPrompt !== null
}

/**
 * Chrome/Android install. Safari/iOS exposes no programmatic install, so the
 * UI must fall back to explicit Share → Add to Home Screen instructions rather
 * than showing a button that cannot work.
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferredInstallPrompt) return 'unavailable'
  const prompt = deferredInstallPrompt
  deferredInstallPrompt = null
  installPromptListeners.forEach((listener) => listener(false))
  try {
    await prompt.prompt()
    return (await prompt.userChoice).outcome
  } catch {
    return 'dismissed'
  }
}

/**
 * Ask the Service Worker to replay the offline queue when connectivity
 * returns. Background Sync is an optimization, never a requirement — Safari
 * has none, so the app also replays on focus/online in the page itself.
 */
export async function requestBackgroundSync(): Promise<boolean> {
  const registration = await currentWorkerServiceWorker()
  const sync = (registration as any)?.sync
  if (!sync?.register) return false
  try {
    await sync.register(OFFLINE_SYNC_TAG)
    return true
  } catch {
    return false
  }
}
